<template>
    <Transition v-if="isLoading"
                name="spinner">
        <div id="cover-spin">
            <span class="loader">
                <span>PROTO</span>
            </span>
        </div>
    </Transition>

    <v-form :name="formProps.formName"
            @keyup.enter="autoSave = false">
        <Tabs value="0"
              scrollable>

            <TabList>
                <Tab value="0">{{ __('Basic data') }}</Tab>
                <Tab value="1">{{ __('Changelog') }}</Tab>
            </TabList>

            <TabPanels>
                <TabPanel value="0">
                    <div class="grid">
                        <FormInputText fieldName="company_name"
                                       label="Company name"
                                       size="12"
                                       validation="required"
                                       :isFormDisabled="isFormDisabled"
                                       :isFocus="true"
                                       v-model:field="crud.company_name" />
                    </div>
                    <div class="grid">
                        <FormSelect fieldName="service_id"
                                    label="Service name"
                                    validation="required"
                                    :isFocus="true"
                                    :isFormDisabled="isFormDisabled"
                                    :options="select?.services"
                                    :change="(event) => onChangeService(event)"
                                    v-model:field="crud.service_id" />
                    </div>
                    <div class="grid">
                        <FormInputText fieldName="agreement_number"
                                       label="Agreement number"
                                       size="4"
                                       validation="required"
                                       :isFormDisabled="isFormDisabled"
                                       v-model:field="crud.agreement_number" />

                        <FormInputText fieldName="location"
                                       label="Location"
                                       size="8"
                                       validation="required"
                                       :isFormDisabled="isFormDisabled"
                                       :isFocus="true"
                                       v-model:field="crud.location" />
                    </div>
                    <div class="grid">
                        <FormCalendar fieldName="start_date"
                                      label="Start date"
                                      size="2"
                                      :isFormDisabled="isFormDisabled"
                                      v-model:field="crud.start_date" />

                        <FormCalendar fieldName="end_date"
                                      label="Finish date"
                                      size="2"
                                      :isFormDisabled="isFormDisabled"
                                      v-model:field="crud.end_date" />

                        <FormInputToggleButton fieldName="is_green"
                                               label="Does the service impact the European Green Deal?"
                                               size="4"
                                               :change="isGreenWasChanged"
                                               :isFormDisabled="isFormDisabled"
                                               v-model:field="crud.is_green" />

                        <FormInputToggleButton fieldName="is_innovation"
                                               label="Has a process or product innovation been introduced?"
                                               size="4"
                                               :change="isInnovationWasChanged"
                                               :isFormDisabled="isFormDisabled"
                                               v-model:field="crud.is_innovation" />
                    </div>

                    <div class="grid">
                        <FormTextarea fieldName="service_description"
                                      label="Service description"
                                      style="margin-bottom:5px"
                                      :keyup="serviceDescriptionWasChanged"
                                      :isFormDisabled="isFormDisabled"
                                      v-model:field="crud.service_description" />

                    </div>
                    <div class="grid"
                         style="margin-bottom:15px">
                        <span>{{ crud.service_description?.length ?? 0 }} / {{ maxDescriptionChars }}</span>
                    </div>

                    <div class="grid">
                        <FormTextarea fieldName="leading_technologies"
                                      label="Lead technologies"
                                      style="margin-bottom:5px"
                                      :keyup="leadingTechnologiesWasChanged"
                                      :isFormDisabled="isFormDisabled"
                                      v-model:field="crud.leading_technologies" />
                    </div>
                    <div class="grid"
                         style="margin-bottom:15px">
                        <span>{{ crud.leading_technologies?.length ?? 0 }} / {{ maxTechnologiedChars }}</span>
                    </div>
                    <div class="grid">
                        <FormTextarea fieldName="used_resources"
                                      label="Used resources"
                                      style="margin-bottom:5px"
                                      :keyup="usedResourcesWasChanged"
                                      :isFormDisabled="isFormDisabled"
                                      v-model:field="crud.used_resources" />
                    </div>
                    <div class="grid"
                         style="margin-bottom:15px">
                        <span>{{ crud.used_resources?.length ?? 0 }} / {{ maxResourcesChars }}</span>
                    </div>
                    <div class="grid"
                         v-if="isGreen">
                        <FormTextarea fieldName="green_description"
                                      label="Impact on the European Green Deal"
                                      style="margin-bottom:5px"
                                      :keyup="greenDescriptionWasChanged"
                                      :isFormDisabled="isFormDisabled"
                                      v-model:field="crud.green_description" />
                    </div>
                    <div class="grid"
                         v-if="isGreen"
                         style="margin-bottom:15px">
                        <span>{{ crud.green_description?.length ?? 0 }} / {{ maxGreenChars }}</span>
                    </div>
                    <div class="grid"
                         v-if="isInnovation">
                        <FormTextarea fieldName="innovation_description"
                                      label="Process or product innovation"
                                      style="margin-bottom:5px"
                                      :keyup="innovationDescriptionWasChanged"
                                      :isFormDisabled="isFormDisabled"
                                      v-model:field="crud.innovation_description" />
                    </div>
                    <div class="grid"
                         v-if="isInnovation"
                         style="margin-bottom:15px">
                        <span>{{ crud.innovation_description?.length ?? 0 }} / {{ maxInnovationChars }}</span>
                    </div>
                    <div class="grid">
                        <FormInputText fieldName="signatory_partner"
                                       label="Signatory Partner"
                                       size="6"
                                       :isFormDisabled="isFormDisabled"
                                       v-model:field="crud.signatory_partner" />

                        <FormInputText fieldName="signatory_client"
                                       label="Signatory Client"
                                       size="6"
                                       :isFormDisabled="isFormDisabled"
                                       v-model:field="crud.signatory_client" />
                    </div>
                </TabPanel>
                <TabPanel v-if="crud.log"
                          value="2">
                    <ChangeLog :log="crud?.log" />
                </TabPanel>
            </TabPanels>
        </Tabs>

        <div class="grid">
            <FormButtons :formProps="props.formProps"
                         :sourceForm="crud"
                         :validation="validation"
                         :buttonsVisibility="buttonsVisibility"
                         :showAfterSave="true"
                         v-model:isFormDisabled="isFormDisabled"
                         v-model:autoSave="autoSave">
                <template #after-in-show="buttonProps">
                    <CustomButton name="printProtocol"
                                  :buttonProps="buttonProps"
                                  :buttonsVisibility="buttonsVisibility"
                                  :click="() => printProtocol()"
                                  style="margin:0 5px"
                                  severity="info">
                        {{ __('Print') }}
                    </CustomButton>
                </template>

            </FormButtons>
        </div>
    </v-form>
</template>

<script setup>
import { ref, onMounted } from 'vue'

import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';

import FormInputText from '@crud/FormInputText.vue'
import FormSelect from '@crud/FormSelect.vue'
import FormButtons from '@crud/FormButtons.vue'
import CustomButton from '@crud/CustomButton.vue'
import FormCalendar from '@crud/FormCalendar.vue'
import FormTextarea from '@crud/FormTextarea.vue'
import FormInputToggleButton from '@crud/FormInputToggleButton.vue'
import { onFilterService, initFormObj, validation } from '@pages/Helper/crud_utils.js'
import ChangeLog from '@crud/ChangeLog.vue'
import { __, getFromRoute, printReport } from '@pages/Helper/utils.js'

// -----------------------------------------------------

const props = defineProps({
    formProps: Object,
})

const isLoading = ref(true);
const isFormDisabled = ref(false)
const autoSave = ref(false)
const buttonsVisibility = ref(null);
const options = ["services"];
const select = ref(null);
const isGreen = ref(false);
const isInnovation = ref(false);

const maxDescriptionChars = 1000;
const maxTechnologiedChars = 500;
const maxResourcesChars = 500;
const maxGreenChars = 1000;
const maxInnovationChars = 1000;

const crud = ref({})

// -----------------------------------------------------

onMounted(async () => {
    const paramsObj = { props, crud, isLoading, options, select, buttonsVisibility, $paramsToSelect: { 'protocol': 'B' } }
    await initFormObj(paramsObj)
    isGreen.value = crud.value.is_green;
    isInnovation.value = crud.value.is_innovation;
})

// -----------------------------------------------------

const isGreenWasChanged = (value) => {
    isGreen.value = value;
    if (!value) {
        crud.value.green_description = null;
    }
}

// --------------------------------------------------

const isInnovationWasChanged = (value) => {
    isInnovation.value = value;
    if (!value) {
        crud.value.innovation_description = null;
    }
}

// -----------------------------------------------------

const serviceDescriptionWasChanged = (value) => {
    if (value && value.length > maxDescriptionChars) {
        crud.value.service_description = value.substring(0, maxDescriptionChars);
    }
}

// -----------------------------------------------------

const leadingTechnologiesWasChanged = (value) => {
    if (value && value.length > maxTechnologiedChars) {
        crud.value.leading_technologies = value.substring(0, maxTechnologiedChars);
    }
}

// -----------------------------------------------------

const usedResourcesWasChanged = (value) => {
    if (value && value.length > maxResourcesChars) {
        crud.value.used_resources = value.substring(0, maxResourcesChars);
    }
}

// -----------------------------------------------------

const greenDescriptionWasChanged = (value) => {
    if (value && value.length > maxGreenChars) {
        crud.value.green_description = value.substring(0, maxGreenChars);
    }
}

// -----------------------------------------------------

const innovationDescriptionWasChanged = (value) => {
    if (value && value.length > maxInnovationChars) {
        crud.value.innovation_description = value.substring(0, maxInnovationChars);
    }
}

const onChangeService = async (event) => {
    if (props.formProps.addMode) {
        const response = await getFromRoute('service-record', { 'id': event.value }, 'get')
        if (response.symbol == "T4.4") {
            crud.value.is_innovation = true;
            isInnovation.value = true;
        }
        if (response && response?.lead_technology) {
            crud.value.leading_technologies = response.lead_technology;
        }
    }
}

// -----------------------------------------------------

const printProtocol = async () => {
    isLoading.value = true;
    if (!crud.value.id) {
        crud.value.id = (await getFromRoute('bprot-last-added', {}, 'get')).id
    }
    const response = await getFromRoute('bprot-print', { 'bprot_id': crud.value.id }, 'post')
    printReport(response)
    isLoading.value = false;
}

// --------------------------------------------------------------------------------------------------------------------------------------------
</script>

<style lang="scss" scoped></style>
